import React, { useState, useEffect } from 'react';
import { Plus, Search, Filter, Edit, Trash2, Tag, Eye, MoreVertical } from 'lucide-react';
import { categoryService } from '../services/categoryService';
import { CategoryResponseDTO, CategoryFilters } from '../types/category';
import { PageResponse } from '../types/api';
import { alertService } from '../services/alertService';
import CategoryModal from '../components/categories/CategoryModal';

const CategoriesPage: React.FC = () => {
  const [categories, setCategories] = useState<CategoryResponseDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CategoryResponseDTO | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize] = useState(10);
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(undefined);
  const [parentFilter, setParentFilter] = useState('');

  useEffect(() => {
    loadCategories();
  }, [currentPage, searchTerm, activeFilter, parentFilter]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        size: pageSize,
        sort: 'nome',
        direction: 'asc' as const,
        filters: {
          ...(searchTerm && { nome: searchTerm }),
          ...(activeFilter !== undefined && { ativo: activeFilter }),
          ...(parentFilter && { categoriaPai: parentFilter })
        } as CategoryFilters
      };

      const response = await categoryService.list(params);
      if (response.success && response.data) {
        setCategories(response.data.content);
        setTotalPages(response.data.totalPages);
        setTotalElements(response.data.totalElements);
      }
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
      alertService.error('Erro ao carregar categorias');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(0);
    loadCategories();
  };

  const handleEdit = (category: CategoryResponseDTO) => {
    setEditingCategory(category);
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    const result = await alertService.confirm(
      'Excluir categoria',
      'Tem certeza que deseja excluir esta categoria?',
      'Sim, excluir',
      'Cancelar'
    );
    
    if (result.isConfirmed) {
      try {
        await categoryService.delete(id);
        alertService.success('Categoria excluída com sucesso!');
        loadCategories();
      } catch (error) {
        console.error('Erro ao excluir categoria:', error);
        alertService.error('Erro ao excluir categoria');
      }
    }
  };

  const handleToggleActive = async (category: CategoryResponseDTO) => {
    try {
      if (category.ativo) {
        await categoryService.deactivate(category.id);
        alertService.success('Categoria desativada com sucesso!');
      } else {
        await categoryService.activate(category.id);
        alertService.success('Categoria ativada com sucesso!');
      }
      loadCategories();
    } catch (error) {
      console.error('Erro ao alterar status da categoria:', error);
      alertService.error('Erro ao alterar status da categoria');
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingCategory(null);
    loadCategories();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setActiveFilter(undefined);
    setParentFilter('');
    setCurrentPage(0);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Tag className="h-6 w-6 sm:h-8 sm:w-8 text-red-600" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Categorias</h1>
            <p className="text-sm sm:text-base text-gray-600">Gerencie as categorias de produtos</p>
          </div>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors w-full sm:w-auto"
        >
          <Plus className="h-4 w-4" />
          <span className="sm:inline">Nova Categoria</span>
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4 sm:items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Buscar categorias
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Digite o nome da categoria..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <div className="flex gap-2 sm:gap-4">
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`flex-1 sm:flex-none px-4 py-2 border rounded-lg flex items-center justify-center gap-2 transition-colors ${
                showFilters
                  ? 'bg-red-50 border-red-200 text-red-700'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Filter className="h-4 w-4" />
              <span className="hidden sm:inline">Filtros</span>
            </button>
            
            <button
              type="submit"
              className="flex-1 sm:flex-none bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
            >
              Buscar
            </button>
          </div>
        </form>

        {/* Filtros expandidos */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={activeFilter === undefined ? '' : activeFilter.toString()}
                  onChange={(e) => setActiveFilter(e.target.value === '' ? undefined : e.target.value === 'true')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="">Todos</option>
                  <option value="true">Ativo</option>
                  <option value="false">Inativo</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Categoria Pai
                </label>
                <input
                  type="text"
                  value={parentFilter}
                  onChange={(e) => setParentFilter(e.target.value)}
                  placeholder="Digite o nome da categoria pai..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={clearFilters}
                className="text-red-600 hover:text-red-700 text-sm font-medium"
              >
                Limpar filtros
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Results Summary */}
      <div className="mb-4">
        <p className="text-sm text-gray-600">
          {loading ? (
            'Carregando...'
          ) : (
            `Mostrando ${categories.length} de ${totalElements} categorias`
          )}
        </p>
      </div>

      {/* Categories Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
          </div>
        ) : categories.length === 0 ? (
          <div className="text-center py-12">
            <Tag className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma categoria encontrada</h3>
            <p className="text-gray-600 mb-4">Não há categorias cadastradas ou que correspondam aos filtros aplicados.</p>
            <button
              onClick={() => setShowModal(true)}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Cadastrar primeira categoria
            </button>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Categoria
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Categoria Pai
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Descrição
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data Criação
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {categories.map((category) => (
                    <tr key={category.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Tag className="h-5 w-5 text-red-600 mr-3" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {category.nome}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {category.categoriaPaiNome || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {category.descricao || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(category.dataCriacao)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          category.ativo
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {category.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleEdit(category)}
                            className="text-red-600 hover:text-red-700 p-1 rounded"
                            title="Editar"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleToggleActive(category)}
                            className={`p-1 rounded ${
                              category.ativo
                                ? 'text-orange-600 hover:text-orange-700'
                                : 'text-green-600 hover:text-green-700'
                            }`}
                            title={category.ativo ? 'Desativar' : 'Ativar'}
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(category.id)}
                            className="text-red-600 hover:text-red-700 p-1 rounded"
                            title="Excluir"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-4">
              {categories.map((category) => (
                <div key={category.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <Tag className="h-4 w-4 text-red-600 mr-2" />
                        <h3 className="text-sm font-medium text-gray-900">
                          {category.nome}
                        </h3>
                      </div>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        category.ativo
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {category.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </div>
                    <div className="flex gap-1 ml-2">
                      <button
                        onClick={() => handleEdit(category)}
                        className="text-red-600 hover:text-red-700 p-2 rounded"
                        title="Editar"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleToggleActive(category)}
                        className={`p-2 rounded ${
                          category.ativo
                            ? 'text-orange-600 hover:text-orange-700'
                            : 'text-green-600 hover:text-green-700'
                        }`}
                        title={category.ativo ? 'Desativar' : 'Ativar'}
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(category.id)}
                        className="text-red-600 hover:text-red-700 p-2 rounded"
                        title="Excluir"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-3 text-xs">
                    <div>
                      <span className="text-gray-500 block">Categoria Pai:</span>
                      <span className="text-gray-900">{category.categoriaPaiNome || '-'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Descrição:</span>
                      <span className="text-gray-900">{category.descricao || '-'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Data Criação:</span>
                      <span className="text-gray-900">{formatDate(category.dataCriacao)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-xs sm:text-sm text-gray-700 order-2 sm:order-1">
            Página {currentPage + 1} de {totalPages}
          </div>
          <div className="flex gap-1 sm:gap-2 order-1 sm:order-2">
            <button
              onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
              disabled={currentPage === 0}
              className="px-2 sm:px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            
            {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
              const pageNum = Math.max(0, Math.min(totalPages - 3, currentPage - 1)) + i;
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-2 sm:px-3 py-2 border rounded-lg text-sm font-medium ${
                    currentPage === pageNum
                      ? 'bg-red-600 border-red-600 text-white'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {pageNum + 1}
                </button>
              );
            })}
            
            <button
              onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
              disabled={currentPage === totalPages - 1}
              className="px-2 sm:px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Próxima
            </button>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <CategoryModal
          category={editingCategory}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
};

export default CategoriesPage;